import classNames from 'classnames';
import type { PropsWithChildren, ReactNode } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSafePreviousNavigate } from '@sorare/core/src/hooks/useSafePreviousNavigate';
import {
  ensureHexColor,
  getRgbColorFromHex,
} from '@sorare/core/src/lib/colors';
import {
  desktopAndAbove,
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { OnboardingStepLayoutHeader } from './OnboardingStepLayoutHeader/index.tsx';
import background from './assets/background.webp';

const Root = styled(Vertical).attrs({ gap: 4 })`
  overflow-x: clip;
  min-height: var(--100vh);
  background:
    linear-gradient(
      rgba(var(--primary-background-color), 0.12),
      rgba(var(--primary-background-color), 0.12)
    ),
    url(${background}) no-repeat center / cover;
`;
const PageContent = styled(Vertical).attrs({ gap: 2.5 })`
  width: 100%;
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    margin: 0 auto auto auto;
    width: 70%;
  }
  @media ${laptopAndAbove} {
    margin: 0 auto auto auto;
    width: 60%;
  }
  @media ${desktopAndAbove} {
    margin: 0 auto auto auto;
    width: 50%;
  }
  &.fullHeight {
    flex: 1;
  }
`;
const ChildrenWrapper = styled(Vertical)`
  width: 100%;
  &.fullHeight {
    flex: 1;
  }
`;
const CtaWrapper = styled(Vertical).attrs({ center: true })`
  margin-top: auto;
  position: sticky;
  bottom: 0;
  padding: var(--quadruple-unit) var(--double-unit) var(--double-unit);
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--cta-background-color) 20%
  );
  @media ${tabletAndAbove} {
    background: unset;
  }
`;

type Props = {
  center?: boolean;
  primaryBackgroundColor?: string;
  ctaBackgroundColor?: string;
  cta?: ReactNode;
  progress: number | null;
  hideHeader?: boolean;
  backUrl?: string;
};
export const OnboardingStepLayout = ({
  center,
  primaryBackgroundColor,
  cta,
  ctaBackgroundColor,
  children,
  progress,
  hideHeader,
  backUrl,
}: PropsWithChildren<Props>) => {
  const goBack = useSafePreviousNavigate(backUrl || '');
  return (
    <Root
      style={
        {
          '--primary-background-color': getRgbColorFromHex(
            ensureHexColor(primaryBackgroundColor || '#3700FF')
          ),
          '--cta-background-color': ctaBackgroundColor || 'transparent',
        } as CSSProperties
      }
    >
      <PageContent className={classNames({ fullHeight: center })}>
        {!hideHeader && (
          <OnboardingStepLayoutHeader
            noBackButton={!backUrl}
            onBack={goBack}
            progress={progress}
            color={primaryBackgroundColor || 'var(--c-white)'}
          />
        )}
        <ChildrenWrapper
          center={center}
          className={classNames({ fullHeight: center })}
        >
          {children}
        </ChildrenWrapper>
      </PageContent>
      {!!cta && <CtaWrapper>{cta}</CtaWrapper>}
    </Root>
  );
};
